import React, { useState, useEffect } from "react";
import CreditHistoryTable from "./CreditHistoryTable";
import CreditHistoryTwoYearPayment from "./CreditHistoryTwoYearPayment";
import CreditHistoryPersonalInfo from "./CreditHistoryPersonalInfo";
import CreditHistoryAddressInfo from "./CreditHistoryAddressInfo";
import CreditHistoryRecentInquiries from "./CreditHistoryRecentInquiries";
import CreditHistoryCreditorContactInfo from "./CreditHistoryCreditorContactInfo";
import { ICreditResponse, IHistory } from "../../../store/credit/types";
import { groupByKey } from "../../../util/common";
import RenderIcons from "../../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";

interface IProps {
    data?: ICreditResponse;
}

const CreditHistory: React.FunctionComponent<IProps> = function (props) {
    const initialHistory: IHistory[] = [];
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [totalPageNo, setTotalPageNo] = useState(0);
    const { t: tLang } = useTranslation();
    const [selectedGroupedHistory, setSelectedGroupedHistory] = useState(initialHistory);

    const showCreditorHistory = (e: any) => {
        const dropdownIndex = parseInt(e.target.value);
        if (props.data != null) {
            setCurrentPageNo(dropdownIndex + 1);

            var mappingKey = "mappingKey";
            if (props.data.creditReport.history != null) {
                for (var history of props.data.creditReport.history) {
                    if (history.mappingKey === "") {
                        mappingKey = "dateReported";
                    }
                    if (history.accountNumber === "") {
                        history.accountNumber = "****";
                    }
                }
            }

            var groupData = groupByKey(props.data.creditReport.history, mappingKey);
            Object.entries(groupData).map(([key, val], index) => {
                if (index === dropdownIndex) {
                    let arrayValue = val as IHistory[];
                    setSelectedGroupedHistory(arrayValue);
                }
            });
        }
    };

    const nextPage = () => {
        if (currentPageNo < totalPageNo) {
            setCurrentPageNo(currentPageNo + 1);
        }
    };

    const previousPage = () => {
        if (currentPageNo > 1) {
            setCurrentPageNo(currentPageNo - 1);
        }
    };

    const selectHistory = () => {
        var mappingKey = "mappingKey";
        if (props.data != null && props.data.creditReport != null) {
            if (props.data.creditReport.history != null) {
                for (var history of props.data.creditReport.history) {
                    if (history.mappingKey === "") {
                        mappingKey = "dateReported";
                    }
                    if (history.accountNumber === "") {
                        history.accountNumber = "****";
                    }
                }
            }
            var groupData = groupByKey(props.data.creditReport.history, mappingKey);
            var itemLength: number = 0;
            Object.entries(groupData).map(([key, val], index) => {
                if (index === currentPageNo - 1) {
                    let arrayValue = val as IHistory[];
                    arrayValue[0].position = index;
                    setSelectedGroupedHistory(arrayValue);
                }
                itemLength++;
            });
            setTotalPageNo(itemLength);
        }
    };

    const getOptionValue = () => {
        if (props.data != null && props.data.creditReport != null) {
            var creditorNames: string[] = [];
            let mappingKey = "mappingKey";

            for (var history of props.data.creditReport.history) {
                if (history.mappingKey === "") {
                    mappingKey = "dateReported";
                }
                if (history.accountNumber === "") {
                    history.accountNumber = "****";
                }
            }

            var groupData = groupByKey(props.data.creditReport.history, mappingKey);
            Object.entries(groupData).map(([key, val], index) => {
                let arrayValue = val as IHistory[];
                creditorNames.push(arrayValue[0].creditorName);
            });

            return (
                <React.Fragment>
                    {creditorNames.map((val, index) => (
                        <option key={"credit_history_" + index} value={index.toString()}>
                            {val}
                        </option>
                    ))}
                </React.Fragment>
            );
        }
    };

    useEffect(selectHistory, [props.data, currentPageNo]);

    return (
        <React.Fragment>
            {selectedGroupedHistory != null && selectedGroupedHistory.length > 0 ? (
                <div className="box offset-padding">
                    <div className="box-header">
                        <div className="box-title-container">
                            <div className="box-title-icon" aria-hidden="true">
                                <RenderIcons iconName="chart-line" />
                            </div>
                            <div className="box-title-text">
                                <h2 className="box-title" id="credit-score-trend-title">
                                    {tLang("credit.creditReport.tabs.creditSummarySection.creditHistorySection.title")} ({currentPageNo} {tLang("credit.creditReport.tabs.creditSummarySection.creditHistorySection.of")} {totalPageNo}
                                    <span className="sr-only"> {tLang("credit.creditReport.tabs.creditSummarySection.creditHistorySection.existingAccounts")}</span>)
                                </h2>
                            </div>
                        </div>
                        <div className="box-description"></div>
                        <div className="box-actions"></div>
                    </div>

                    <div className="box-content">
                        <div className="row between-xs" id="credit-report-account-switcher">
                            <div className="col-xs-6 col-sm">
                                <h3 className="content-sub-title">
                                    <span className="sr-only">{tLang("credit.creditReport.tabs.creditSummarySection.creditHistorySection.viewDetailsForAccount")} </span>
                                    {selectedGroupedHistory[0].creditorName}
                                </h3>
                            </div>
                            <div className="col-xs-6 col-sm text-align-right">
                                <button type="button" className="btn-default btn-no-padding btn-icon-only" disabled={false} onClick={() => previousPage()} aria-label="Previous Account">
                                    <span className="btn-icon">
                                        <em className="las la-arrow-left"></em>
                                    </span>
                                    <span className="btn-text">{tLang("credit.creditReport.tabs.creditSummarySection.creditHistorySection.previousAccount")}</span>
                                </button>
                                <button type="button" className="btn-default btn-no-padding btn-icon-only" onClick={() => nextPage()} aria-label="Next Account">
                                    <span className="btn-icon">
                                        <em className="las la-arrow-right"></em>
                                    </span>
                                    <span className="btn-text">{tLang("credit.creditReport.tabs.creditSummarySection.creditHistorySection.nextAccount")}</span>
                                </button>
                            </div>
                            <div className="col-xs-12 col-sm flex-grow-null">
                                <div className="form-row no-margin">
                                    <div className="form-label">
                                        <label htmlFor="switch-account" className="form-item-required sr-only">
                                            {tLang("credit.creditReport.tabs.creditSummarySection.creditHistorySection.switchAccount")}
                                        </label>
                                    </div>
                                    <div className="form-input">
                                        <div className="form-input-control">
                                            {props.data != null ? (
                                                <select
                                                    className="inputbox"
                                                    value={selectedGroupedHistory[0].position.toString()}
                                                    id="switch-account"
                                                    onChange={(e) => showCreditorHistory(e)}
                                                >
                                                    {getOptionValue()}
                                                </select>
                                            ) : (
                                                ""
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <React.Fragment>
                            <div className="margin-vertical-s"></div>

                            {selectedGroupedHistory != null ? <CreditHistoryTable groupData={selectedGroupedHistory} /> : null}

                            <div className="margin-vertical-l"></div>

                            {props.data != null && props.data.creditReport != null ? (
                                <React.Fragment>
                                    <CreditHistoryTwoYearPayment groupData={selectedGroupedHistory} />
                                    <CreditHistoryPersonalInfo data={props.data.creditReport.personalInformation} />
                                    <CreditHistoryAddressInfo data={props.data.creditReport.addressInformation} />
                                    <CreditHistoryRecentInquiries data={props.data.creditReport.recentInquiries} />
                                    <CreditHistoryCreditorContactInfo data={props.data.creditReport.contactInformation} />
                                </React.Fragment>
                            ) : (
                                ""
                            )}
                        </React.Fragment>
                    </div>
                </div>
            ) : (
                ""
            )}
        </React.Fragment>
    );
};

export default CreditHistory;
